/**
 * Checklist Answer Hooks
 *
 * React hooks for saving, clearing, and generating checklist item answers.
 * These hooks wrap backend mutations/actions and provide consistent error handling
 * and success feedback via useApiMutation.
 */

import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";

/**
 * Hook for saving or updating a checklist item answer
 *
 * Wraps `api.checklist_answer_mutations.saveAnswer`
 * Upserts by question.order - replaces existing answer if one exists for that order
 */
export function useSaveChecklistAnswer() {
  return useApiMutation(api.checklist_answer_mutations.saveAnswer, {
    name: "save-checklist-answer",
    silentSuccess: true,
  });
}

/**
 * Hook for clearing a specific answer from a checklist item
 *
 * Wraps `api.checklist_answer_mutations.clearAnswer`
 * Removes the answer with the given questionOrder from the answers array
 */
export function useClearChecklistAnswer() {
  return useApiMutation(api.checklist_answer_mutations.clearAnswer, {
    name: "clear-checklist-answer",
    silentSuccess: true,
  });
}

/**
 * Hook for AI-generating an answer from linked evidence documents
 *
 * Wraps `api.checklist_answer_mutations.generateAnswerFromEvidence`
 * Enqueues background workpool job; the answer is saved to DB automatically.
 * Returns { hasEvidence: false } immediately if no documents are linked.
 */
export function useGenerateAnswerFromEvidence() {
  return useApiMutation(
    api.checklist_answer_mutations.generateAnswerFromEvidence,
    {
      name: "generate-answer-from-evidence",
      silentSuccess: true,
      silentErrors: true,
    },
  );
}

/**
 * Hook for batch-generating AI answers for all unanswered questions
 *
 * Wraps `api.checklist_answer_mutations.batchGenerateAnswers`
 * Enqueues all unanswered questions to the AI workpool.
 * Returns { hasEvidence, enqueuedCount, totalUnanswered }.
 */
export function useBatchGenerateAnswers() {
  return useApiMutation(api.checklist_answer_mutations.batchGenerateAnswers, {
    name: "batch-generate-answers",
    silentSuccess: true,
    silentErrors: true,
  });
}
